import React from 'react';
import { SectionProps } from '../types';
import SectionCompetitorResults from '../shared/SectionCompetitorResults';
import SectionMessage from '../shared/SectionMessage';
import SectionTitle from '../shared/SectionTitle';
import SectionTooltip from '../shared/SectionTooltip';
import { useLazyFetchCompetitorDomainsQuery } from '../../../../competitorDomains/service';
import { Icon } from 'antd';
import { SectionResult } from '../../../types';
import { getUrl } from '../utils';

export default function CompetitorDomainsSection({ sectionKey, title, url, relevance, tooltip, result, competitorsResults, passRate }: SectionProps) {
    const hasCompetitors = competitorsResults && Object.keys(competitorsResults).length > 0;

    let renderResultComponent: (result: SectionResult) => React.ReactNode = () => null;

    if (Object.hasOwn(result, 'competitors') || Object.hasOwn(result, 'competitor_domains')) {
        renderResultComponent = (result) => (
            <CompetitorDomainsResult
                competitorDomains={result.competitor_domains}
                competitors={result.competitors}
            />
        );
    } else if (Object.hasOwn(result, 'location_name')) {
        renderResultComponent = (result) => (
            <CompetitorDomainsButton
                domain={result.domain}
                locationName={result.location_name}
                languageCode={result.language_code}
            />
        );
    }

    return (
        <div
            id={sectionKey}
            className="report-element default"
        >
            <SectionTitle title={title} />

            <div className="report-element-description">
                <SectionMessage
                    url={url}
                    status={result.status}
                    relevance={relevance}
                    message={result.message}
                    hasCompetitors={hasCompetitors}
                />

                {renderResultComponent(result)}

                {hasCompetitors && (
                    <SectionCompetitorResults
                        results={competitorsResults}
                        relevance={relevance}
                        render={renderResultComponent}
                        hideMessage={true}
                    />
                )}
            </div>

            <SectionTooltip tooltip={tooltip} />
        </div>
    );
}

type CompetitorDomainsResultProps = {
    competitorDomains?: string[];
    competitors?: {
        domain: string;
        score?: number;
        rank?: number;
    }[];
};

function CompetitorDomainsResult({ competitorDomains, competitors }: CompetitorDomainsResultProps) {
    if (competitorDomains && competitorDomains.length > 0) {
        return (
            <div className="description-meta">
                <ul>
                    {competitorDomains.map((domain) => (
                        <li key={domain}>
                            <a
                                href={getUrl(domain)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {domain}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        );
    } else if (competitors && competitors.length > 0) {
        // Old reports have score instead of rank
        const unitOfValue = Object.hasOwn(competitors[0], 'rank') ? 'rank' : 'score';

        return (
            <div className="stats-table-container">
                <header>
                    <div className="col">Competitor domain</div>
                    <div className="col">Domain {unitOfValue}</div>
                </header>

                {competitors.map((competitor, idx) => {
                    return (
                        <div
                            className="row"
                            key={idx}
                        >
                            <div className="col">
                                <a
                                    href={getUrl(competitor['domain'])}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {competitor['domain']}
                                </a>
                            </div>
                            <div className="col">{competitor[unitOfValue]}</div>
                        </div>
                    );
                })}
            </div>
        );
    } else {
        return (
            <div className="description-meta">
                <span>There are no indexed competitors for this domain!</span>
            </div>
        );
    }
}

type CompetitorDomainsButtonProps = {
    domain: string;
    locationName: string;
    languageCode: string;
};

function CompetitorDomainsButton({ domain, locationName, languageCode }: CompetitorDomainsButtonProps) {
    const [fetchCompetitorDomains, { isLoading: isLoadingCompetitorDomains, isSuccess: isCompetitorDomainsFetchSuccess, data: competitorDomains }] =
        useLazyFetchCompetitorDomainsQuery();

    const handleShowCompetitorDomainsClicked = () => {
        fetchCompetitorDomains({ domain: domain, locationName: locationName, languageCode: languageCode }, true);
    };

    const orderedCompetitorDomains = competitorDomains && [...competitorDomains.competitors].sort((a, b) => b.rank - a.rank);

    const showCompetitorDomains = isCompetitorDomainsFetchSuccess && !!orderedCompetitorDomains;

    return (
        <>
            {showCompetitorDomains && <CompetitorDomainsResult competitors={orderedCompetitorDomains} />}

            {!showCompetitorDomains && (
                <div
                    className="description-meta"
                    style={{ marginTop: '8px' }}
                >
                    <span
                        className={`inline-modal-trigger fake-link full-list ${isLoadingCompetitorDomains ? 'loading' : ''}`}
                        onClick={handleShowCompetitorDomainsClicked}
                    >
                        <Icon type={isLoadingCompetitorDomains ? 'loading' : 'caret-right'} /> See results list
                    </span>
                </div>
            )}
        </>
    );
}
